import { useQuery } from "@apollo/client";
import { GET_ASSETS_BY_ORGANIZATION } from "../gql";
import type {
  AssetsOrderBy,
  AssetFilter,
  Asset,
  AssetsConnection,
} from "@casey/db-types";

interface UseAssetsOptions {
  first?: number;
  after?: string;
  filter?: AssetFilter;
  orderBy?: AssetsOrderBy[];
  skip?: boolean;
}

interface UseAssetsResult {
  assets: Asset[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  loadMore: () => void;
  hasMore: boolean;
  totalCount: number;
}

export function useAssets(
  organizationId: string,
  options: UseAssetsOptions = {},
): UseAssetsResult {
  const { first = 20, after, filter, orderBy, skip } = options;

  // Always filter out archived assets, merging with any provided filter
  const combinedFilter: AssetFilter = {
    ...(filter || {}),
    archivedAt: { isNull: true },
  };

  const { data, loading, error, refetch, fetchMore } = useQuery<{
    assets: AssetsConnection;
  }>(GET_ASSETS_BY_ORGANIZATION, {
    variables: {
      organizationId,
      first,
      ...(after && { after }),
      filter: combinedFilter,
      ...(orderBy && { orderBy }),
    },
    skip: skip || !organizationId,
    errorPolicy: "all",
  });

  const assets = data?.assets?.nodes || [];
  const pageInfo = data?.assets?.pageInfo;
  const totalCount = data?.assets?.totalCount || 0;

  const loadMore = () => {
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.assets) {
            return prev;
          }

          const newNodes = fetchMoreResult.assets.nodes;
          const newPageInfo = fetchMoreResult.assets.pageInfo;

          return {
            ...prev,
            assets: {
              ...prev.assets!,
              nodes: [...prev.assets!.nodes, ...newNodes],
              pageInfo: newPageInfo,
            },
          };
        },
      });
    }
  };

  return {
    assets: assets as Asset[],
    loading,
    error: error || null,
    refetch,
    loadMore,
    hasMore: pageInfo?.hasNextPage || false,
    totalCount,
  };
}
